import React from "react";
import { CreditCard, Wallet, Heart, Car, Briefcase, PiggyBank, LineChart, Shield, Building2, Banknote } from "lucide-react";

// Category → icon + accent colour. Falls back to a neutral pair.
const CATEGORY_META = {
  "credit cards": { icon: CreditCard, from: "#064E3B", via: "#047857", to: "#10B981" },
  "credit card":  { icon: CreditCard, from: "#064E3B", via: "#047857", to: "#10B981" },
  "savings account": { icon: PiggyBank, from: "#1E3A8A", via: "#2563EB", to: "#3B82F6" },
  "savings":      { icon: PiggyBank, from: "#1E3A8A", via: "#2563EB", to: "#3B82F6" },
  "personal loan": { icon: Wallet,   from: "#7C2D12", via: "#C2410C", to: "#F97316" },
  "business loan": { icon: Briefcase, from: "#374151", via: "#4B5563", to: "#6B7280" },
  "home loan":     { icon: Building2, from: "#155E75", via: "#0E7490", to: "#06B6D4" },
  "loan":          { icon: Wallet,    from: "#7C2D12", via: "#C2410C", to: "#F97316" },
  "loans":         { icon: Wallet,    from: "#7C2D12", via: "#C2410C", to: "#F97316" },
  "health insurance": { icon: Heart,  from: "#9F1239", via: "#BE123C", to: "#E11D48" },
  "term insurance":   { icon: Shield, from: "#581C87", via: "#7E22CE", to: "#A855F7" },
  "life insurance":   { icon: Shield, from: "#581C87", via: "#7E22CE", to: "#A855F7" },
  "motor insurance":  { icon: Car,    from: "#0C4A6E", via: "#0369A1", to: "#0EA5E9" },
  "insurance":        { icon: Shield, from: "#581C87", via: "#7E22CE", to: "#A855F7" },
  "demat":         { icon: LineChart, from: "#365314", via: "#65A30D", to: "#84CC16" },
  "demat account": { icon: LineChart, from: "#365314", via: "#65A30D", to: "#84CC16" },
  "investment":    { icon: LineChart, from: "#365314", via: "#65A30D", to: "#84CC16" },
};

const DEFAULT_META = { icon: Banknote, from: "#3F3F46", via: "#52525B", to: "#71717A" };

function categoryMeta(category) {
  if (!category) return DEFAULT_META;
  const k = String(category).toLowerCase().trim();
  return CATEGORY_META[k] || DEFAULT_META;
}

function initialsFor(bank, name) {
  const src = (bank && bank.trim()) ? bank.trim() : (name || "Crece");
  // First letter of each word, max 3
  const words = src.split(/\s+/).filter(Boolean).slice(0, 3);
  return words.map(w => w[0]).join("").toUpperCase();
}

/**
 * Smart product image:
 *  - If product has a non-empty `image` URL → render it as <img>
 *  - Otherwise → render a generated SVG placeholder with the bank initials,
 *    a category-coloured gradient and a subtle category icon.
 *
 * Use exactly like an <img>: className controls the wrapping box.
 */
export function ProductImage({ product, className = "" }) {
  const url = (product?.image || "").trim();
  if (url) {
    // Real image — render with fallback on error to the placeholder.
    return (
      <img
        src={url}
        alt={product?.name || "Product"}
        className={className}
        onError={(e) => {
          // Replace broken image with placeholder by re-rendering this component as fallback.
          e.currentTarget.style.display = "none";
          const sib = e.currentTarget.nextElementSibling;
          if (sib) sib.style.display = "";
        }}
      />
    );
  }
  return <ProductImagePlaceholder product={product} className={className} />;
}

export function ProductImagePlaceholder({ product, className = "", style = {} }) {
  const meta = categoryMeta(product?.category);
  const Icon = meta.icon;
  const initials = initialsFor(product?.bank, product?.name);
  // Deterministic small horizontal offset for visual variety per product
  const offset = (() => {
    const s = (product?.id || product?.name || "x");
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) & 0xffff;
    return (h % 20) - 10; // -10..+10 %
  })();

  return (
    <div
      className={`${className} relative flex items-center justify-between text-white overflow-hidden`}
      style={{
        background: `linear-gradient(135deg, ${meta.from} 0%, ${meta.via} 50%, ${meta.to} 100%)`,
        ...style,
      }}
      aria-label={product?.name ? `${product.name} placeholder` : "Product placeholder"}
    >
      {/* Decorative circle */}
      <div
        className="absolute rounded-full opacity-20"
        style={{
          width: "70%",
          aspectRatio: "1",
          right: `${-20 + offset}%`,
          top: "-20%",
          background: "radial-gradient(circle, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 70%)",
        }}
      />
      <div
        className="absolute rounded-full opacity-10"
        style={{
          width: "40%",
          aspectRatio: "1",
          left: `${-10 + offset}%`,
          bottom: "-15%",
          background: "rgba(255,255,255,0.6)",
        }}
      />
      {/* Big initials */}
      <div className="relative z-10 pl-6 flex flex-col gap-1">
        <div className="font-display text-4xl font-black tracking-tight leading-none">
          {initials || "C"}
        </div>
        {product?.bank && (
          <div className="text-[10px] uppercase tracking-[0.18em] opacity-80 font-bold truncate max-w-[140px]">
            {product.bank}
          </div>
        )}
      </div>
      {/* Category icon, large + faded */}
      <Icon className="relative z-10 mr-6 w-14 h-14 opacity-70" strokeWidth={1.5} />
    </div>
  );
}
